import type { Bootgrid } from "./bootgrid";
import { ViewManager } from "./ViewManager/ViewManager";
import { GridView, ViewManagerView } from "./ViewManager/viewManagerTypes";
import { gridViewApi } from "./ViewManager/ViewApi/GridViewApi";
import { BootgridStateConstructor } from "./ViewManager/bootgridStateConstructor";
import { EmptyGuid } from "../emptyGuid";
import { luscinia } from "../luscinia";

export async function initGridViewManager(this: Bootgrid, initialViewId: string) {
    if (this.container === undefined || this.container === null) return;
    if (this.container.querySelector(".view-manager") !== null) return;
    if (this.isWizardPage()) return;

    this.viewManager = new ViewManager({
        addButtonHandler: (e: MouseEvent, viewManager: ViewManager) => {
            luscinia.showConfirmationDialogWithExtraInput(
                "Add new view",
                "you are about to add new view please provide a name for the view ",
                async (inputValue) => {
                    const gridView = constructGridView.call(this, inputValue);
                    const savedViewId = await gridViewApi.saveView(gridView);
                    await viewManager.refreshOptions();
                    viewManager.setSelectedViewId(savedViewId);
                }
            );
        },
        saveButtonHandler: (e: MouseEvent, viewManager: ViewManager) => {
            this.incrementLoading();
            const viewId = viewManager.getSelectedViewId();
            let view = this.views.find((view) => view.Id === viewId);
            if (view) {
                let isTempViewSave: boolean = false;
                if (view.Name.startsWith("luscinia-temp-view-")) {
                    const originalViewId = view.Name.replace("luscinia-temp-view-", "");
                    view = this.views.find((view) => view.Id === originalViewId);
                    isTempViewSave = true;
                }

                luscinia.showConfirmationDialog("Save view", `your changes will be saved to ${view.Name}`, async () => {
                    const gridView = constructGridView.call(this, view.Name);
                    await gridViewApi.updateView(view.Id, gridView);

                    if (isTempViewSave) {
                        await viewManager.refreshOptions();
                        viewManager.setSelectedViewId(view.Id);
                    }
                });
            }
            this.decrementLoading();
        },
        editButtonHandler: (e: MouseEvent, viewManager: ViewManager) => {
            this.incrementLoading();
            luscinia.showConfirmationDialogWithExtraInput("Rename view", "please provide a new name for the view ", async (inputValue) => {
                const viewId = viewManager.getSelectedViewId();
                if (viewId) {
                    try {
                        await gridViewApi.updateViewName(viewId, inputValue);
                        await viewManager.refreshOptions();
                        viewManager.setSelectedViewId(viewId);
                    } catch (error) {
                        console.error(error);
                    }
                }
            });
            this.decrementLoading();
        },
        shareButtonHandler: (e, viewManager) => {
            this.incrementLoading();
            const viewId = viewManager.getSelectedViewId();
            if (viewId && viewId !== "default") {
                const view = this.views.find((view) => view.Id === viewId);
                const setViewPrivate = async () => {
                    await gridViewApi.setViewPrivate(viewId, !view.IsPrivate)
                    await viewManager.refreshOptions();
                    viewManager.setSelectedViewId(viewId);
                };
                setViewPrivate();
            }
            this.decrementLoading();             
        },
        deleteButtonHandler: (e: MouseEvent, viewManager: ViewManager) => {
            this.incrementLoading();
            const viewId = viewManager.getSelectedViewId();
            if (viewId) {
                luscinia.showConfirmationDialog("Delete view", "you are about to delete a view are you sure?", async () => {
                    await gridViewApi.deleteView(viewId);
                    await viewManager.refreshOptions();
                    viewManager.setSelectedViewId("default");
                });
            }
            this.decrementLoading();
        },
        select2ChangeHandler: async (viewManager: ViewManager) => {
            this.incrementLoading();
            const selectedViewId = viewManager.getSelectedViewId();
            if (!selectedViewId) return;

            this.views.map((view) => (view.IsActive = view.Id === selectedViewId));

            const selectedView = this.views.find((view) => view.Id === selectedViewId);
            viewManager.updateButtonVisibilities(selectedView.ButtonVisibilities);
            viewManager.setIsPrivate(selectedView.IsPrivate);

            if (selectedViewId != "default" && selectedViewId != null && selectedViewId != undefined) {
                const view = await gridViewApi.getView(selectedViewId);
                this.bootgridState = view.BootgridState;
                await this.reRender();
            }
            if (selectedViewId === "default") {
                const defaultView = await gridViewApi.getDefaultGridView(this.tableName);
                this.bootgridState = defaultView.BootgridState;
                await this.reRender();
            }
            this.decrementLoading();
        },
        getViews: async (viewManager: ViewManager) => {
            this.incrementLoading();
            const gridViews = await gridViewApi.getViews(this.tableName);

            const defaultView: ViewManagerView = {
                Id: "default",
                UserId: "default",
                Name: "Default",
                DisplayName: "Default",
                IsActive: false,
                IsVisible: true,
                IsPrivate: true,
                IsDefault: true,
                ButtonVisibilities: {
                    ShowAddButton: true,
                    ShowDeleteButton: false,
                    ShowEditButton: false,
                    ShowSaveButton: false,
                    ShowShareButton: false,
                },
            };

            gridViews.unshift(defaultView);

            gridViews.forEach((view) => {
                if (view.Name.startsWith("luscinia-temp-view-")) {
                    const originalViewId = view.Name.replace("luscinia-temp-view-", "");
                    const originalViewName = gridViews.find((v) => v.Id === originalViewId)?.Name;
                    view.DisplayName = originalViewName + "*";
                    if (originalViewId === "default") {
                        view.IsDefault = true;
                        view.ButtonVisibilities = {
                            ShowAddButton: true,
                            ShowDeleteButton: false,
                            ShowEditButton: false,
                            ShowSaveButton: false,
                            ShowShareButton: false,
                        };
                    } else {
                        view.ButtonVisibilities = {
                            ShowAddButton: true,
                            ShowDeleteButton: false,
                            ShowEditButton: false,
                            ShowSaveButton: true,
                            ShowShareButton: false,
                        };
                    }
                } else {
                    view.DisplayName = view.Name;
                    view.IsVisible = true;
                    if (!view.IsDefault) {
                        view.IsDefault = false;
                        view.ButtonVisibilities = {
                            ShowAddButton: true,
                            ShowDeleteButton: true,
                            ShowEditButton: true,
                            ShowSaveButton: true,
                            ShowShareButton: true,
                        };
                    }
                }
            });

            const userId = this.$bootgrid.data('luscinia-userid');
            gridViews.map((view) => {
                view.IsActive = view.Id === initialViewId;
                view.ButtonVisibilities.ShowShareButton = view.UserId === userId && !view.Name.startsWith("luscinia-temp-view-");
                view.ButtonVisibilities.ShowDeleteButton = view.UserId === userId && !view.Name.startsWith("luscinia-temp-view-");
                view.ButtonVisibilities.ShowEditButton = view.UserId === userId && !view.Name.startsWith("luscinia-temp-view-");
                view.ButtonVisibilities.ShowSaveButton = view.UserId === userId && !(view.Name === "luscinia-temp-view-default");
            });

            this.views = gridViews;
            this.decrementLoading();
            return gridViews;
        },
    });

    const viewManagerContainer = document.createElement("div");
    viewManagerContainer.classList.add("view-manager-container");
    this.container.querySelector(".actionBar-right").appendChild(viewManagerContainer);
    this.viewManager.init(viewManagerContainer);
}

export function constructGridView(this: Bootgrid, gridViewName: string): GridView {
    const bootgridProcessor = new BootgridStateConstructor(this);
    const gridView: GridView = {
        Id: EmptyGuid.value,
        UserId: EmptyGuid.value,
        ParentName: this.tableName,
        Name: gridViewName,
        IsPrivate: true,
        BootgridState: bootgridProcessor.constructBootgridState(),
    };

    return gridView;
}

export async function stateChangeHandler(this: Bootgrid) {
    if (this.isWizardPage()) return;
    let selectedViewId = this.viewManager.getSelectedViewId();
    if (!selectedViewId) return;
    const selectedView = this.views.find((view) => view.Id === selectedViewId);
    this.incrementLoading();
    if (!selectedView.Name.startsWith("luscinia-temp-view-")) {
        const tempViewName = `luscinia-temp-view-${selectedView.Id}`;
        const tempView = constructGridView.call(this, tempViewName);
        tempView.Id = await gridViewApi.saveView(tempView);
        await this.viewManager.refreshOptions();
        this.viewManager.setSelectedViewId(tempView.Id, false);

        this.views.map((view) => (view.IsActive = view.Id === tempView.Id));
        this.viewManager.updateButtonVisibilities(selectedView.ButtonVisibilities);
    } else {
        const tempView = constructGridView.call(this, selectedView.Name);
        await gridViewApi.updateView(selectedView.Id, tempView);
    }
    this.decrementLoading();
}
